import { showUserFriendlyError, getErrorType } from "./userFriendlyErrors";

/**
 * Centralized error reporting and tracking system
 * Uses user-friendly error messages for better UX
 */

export type ErrorCategory = 'auth' | 'network' | 'validation' | 'payment' | 'unknown';

interface ErrorContext {
  category?: ErrorCategory;
  userId?: string;
  workspaceId?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

class ErrorReporter {
  static report(error: Error, context?: ErrorContext, onRetry?: () => void) {
    // Log to console with context
    console.error('[APP_ERROR]', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      online: navigator.onLine
    });
    
    // TODO: Send to Sentry/LogRocket
    // Sentry.captureException(error, { extra: context });
    
    // Show user-friendly toast
    showUserFriendlyError(error, undefined, onRetry);
  }
  
  static reportWithToast(error: Error, customMessage?: string, context?: ErrorContext, onRetry?: () => void) {
    // Log the actual error
    console.error('[APP_ERROR]', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      context,
      timestamp: new Date().toISOString()
    });
    
    // Show user-friendly toast with custom message if provided
    showUserFriendlyError(error, customMessage, onRetry);
  }
  
  static warn(message: string, context?: ErrorContext) {
    console.warn('[APP_WARNING]', {
      message,
      context,
      timestamp: new Date().toISOString()
    });
    
    // TODO: Send to monitoring service
  }

  /**
   * Handle edge function errors with user-friendly feedback
   */
  static async handleEdgeFunctionError(error: any, context?: ErrorContext, onRetry?: () => void) {
    // Try to extract error details from response body if available
    let errorDetails = error;
    
    // If error has a response body, try to parse it
    if (error?.context && typeof error.context === 'object') {
      errorDetails = { ...error, ...error.context };
    }
    
    // Log error details
    console.error('[EDGE_FUNCTION_ERROR]', {
      error: errorDetails,
      context,
      timestamp: new Date().toISOString()
    });

    // TODO: Send to Sentry/LogRocket
    // Sentry.captureException(error, { extra: context });

    // Show user-friendly error message
    showUserFriendlyError(errorDetails, undefined, onRetry);
  }
}

export { ErrorReporter };
